import { Avatar, Box, Group, ScrollArea, SegmentedControl, Text, ThemeIcon, UnstyledButton, Space } from '@mantine/core'
import { useAuthStore } from '../stores/auth'
import classes from './Navigation.module.css'
import linkClasses from './NavBarLinksGroup.module.css'
import { IconType } from 'react-icons'
import { TbKey, TbLayoutDashboardFilled, TbLogout2, TbPhone, TbUsers, TbRoute, TbTool } from 'react-icons/tb'
import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { LinksGroup } from './NavBarLinksGroup'
import { useTranslation } from 'react-i18next'

import { hasAnyPermission } from '@/utils/permissions'
import { AccessCategory } from '@/api/main/types.gen'
import { UserIntrospectionResponse } from '@/api/main/types.gen'
import { MAP_SNAPSHOTS_ENABLED } from '@/constants'

type NavSection = 'system' | 'users'

const filterNavigationTab = (
  item: NavigationTab,
  userPermissions: UserIntrospectionResponse | null
): NavigationTab | null => {
  const filteredLinks = item.links
    ? item.links.filter(
        link => !link.permission || hasAnyPermission(userPermissions, link.permission as AccessCategory)
      )
    : undefined

  if (item.links) {
    if (filteredLinks && filteredLinks.length > 0) {
      return { ...item, links: filteredLinks }
    }
    return null
  } else {
    if (!item.permission || hasAnyPermission(userPermissions, item.permission as AccessCategory)) {
      return item
    }
    return null
  }
}

export type NavigationTab = {
  label: string
  icon: IconType
  link?: string
  permission: AccessCategory | null
  initiallyOpened?: boolean
  links?: {
    label: string
    link: string
    permission?: AccessCategory | null
  }[]
}

export type NavigationTabs = {
  system: NavigationTab[]
  users: NavigationTab[]
}

export const navigationTabs: NavigationTabs = {
  system: [
    {
      label: 'home',
      icon: TbLayoutDashboardFilled,
      link: '/',
      permission: null,
    },
    {
      label: 'dispatching',
      icon: TbPhone,
      initiallyOpened: true,
      permission: null,
      links: [
        { link: '/hotline-calls', label: 'hotlineCalls', permission: 'hotline' },
        {
          link: '/vehicles',
          label: 'vehicles',
          permission: 'vehicles',
        },
        {
          link: '/unexpected-events',
          label: 'unexpectedEvents',
          permission: 'unexpected_events',
        },
      ],
    },
    {
      label: 'people',
      icon: TbUsers,
      initiallyOpened: true,
      permission: null,
      links: [
        { link: '/shifts', label: 'shifts', permission: 'shifts' },
        {
          link: '/drivers',
          label: 'drivers',
          permission: 'drivers',
        },
      ],
    },
    {
      label: 'driving',
      icon: TbRoute,
      initiallyOpened: true,
      permission: null,
      links: [
        { link: '/routes', label: 'routes', permission: 'routes' },
        { link: '/drive-statistics', label: 'driveStatistics', permission: 'drive_statistics' },
        { link: '/driver-statistics', label: 'driverStatistics', permission: 'drive_statistics' },
        { link: '/route-statistics', label: 'routeStatistics', permission: 'drive_statistics' },
        { link: '/scheduling', label: 'scheduling', permission: 'scheduling' },
      ],
    },
    ...(MAP_SNAPSHOTS_ENABLED
      ? [
          {
            label: 'tools',
            icon: TbTool,
            initiallyOpened: true,
            permission: null,
            links: [{ link: '/map-snapshots', label: 'mapSnapshots', permission: 'map_snapshot' }],
          } as NavigationTab,
        ]
      : []),
  ],
  users: [
    {
      link: '/change-password',
      label: 'resetPassword',
      icon: TbKey,
      permission: 'users',
    },
  ],
}

export function Navigation() {
  const { user, logout, userPermissions } = useAuthStore()
  const navigate = useNavigate()
  const { t } = useTranslation('nav')
  const [section, setSection] = useState<NavSection>('system')

  const filteredTabs = useMemo(() => {
    const filtered = { ...navigationTabs }

    filtered.system = navigationTabs.system
      .map(item => filterNavigationTab(item, userPermissions))
      .filter((item): item is NavigationTab => item !== null)

    filtered.users = navigationTabs.users.filter(
      item => !item.permission || hasAnyPermission(userPermissions, item.permission as AccessCategory)
    )

    return filtered
  }, [userPermissions])

  const hasSystemItems = filteredTabs.system.length > 0
  const hasUsersItems = filteredTabs.users.length > 0

  const currentSection = useMemo(() => {
    if (section === 'system' && !hasSystemItems && hasUsersItems) {
      return 'users'
    }
    if (section === 'users' && !hasUsersItems && hasSystemItems) {
      return 'system'
    }
    return section
  }, [section, hasSystemItems, hasUsersItems])

  if (!user) return null

  const links = filteredTabs[currentSection].map(item => (
    <LinksGroup
      key={item.label}
      {...item}
      label={t(item.label as Parameters<typeof t>[0])}
      mainLink={item.link}
      links={item.links?.map(l => ({ ...l, label: t(l.label as Parameters<typeof t>[0]) }))}
    />
  ))

  const version = import.meta.env.VITE_VCS_REVISION ?? null

  return (
    <nav className={classes.navbar}>
      <Group gap="xs" mb="xs" justify="space-between">
        <Avatar size="md" radius="xl" color="blue" variant="filled">
          {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
        </Avatar>
        <Box flex={1}>
          <Text size="xs" c="dimmed" mb={4}>
            {user.email}
          </Text>
          <Text size="xs" c="dimmed" mb={4}>
            {user.role}
          </Text>
        </Box>
      </Group>

      <SegmentedControl
        value={currentSection}
        onChange={value => setSection(value as NavSection)}
        transitionTimingFunction="ease"
        fullWidth
        data={[
          ...(hasSystemItems ? [{ label: t('system'), value: 'system' }] : []),
          ...(hasUsersItems ? [{ label: t('users'), value: 'users' }] : []),
        ]}
      />

      <ScrollArea className={classes.links} scrollbars="y">
        <div className={classes.linksInner}>{links}</div>
      </ScrollArea>

      <div className={classes.footer}>
        <UnstyledButton
          className={linkClasses.control}
          onClick={event => {
            event.preventDefault()
            logout()
            navigate({ to: '/login' })
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <ThemeIcon variant="light" size={30} className={linkClasses.icon}>
              <TbLogout2 size={18} />
            </ThemeIcon>
            <Box ml="md">{t('logout')}</Box>
          </Box>
        </UnstyledButton>

        <Box flex={1}>
          <Space h="xl" />
          <Text size="xs" c="dimmed" ta="center" mt="auto">
            {version}
          </Text>
        </Box>
      </div>
    </nav>
  )
}
